// AI 판정 (§10). 제출 시점에 Gemini를 한 번 호출.
// 지금은 인터페이스 + 목 + 오프라인 폴백만. 실제 Vertex 호출은 callVertex에 채운다.

import type { Archetype, Judgement, Lang, RawSignals } from "./types";
import { localCrisisTrigger } from "./crisis";
// off_scope 정적 문구는 prompt.ts에 단일 출처(서버 프록시와 공유). 목/실서버가 같은 문구를 쓴다.
import { OFF_SCOPE_STEP_BACK as STEP_BACK } from "./prompt";

export interface JudgeInput {
  score: number; // 행동×내용 전의 행동 점수(참고용)
  signals: RawSignals;
  archetype: Archetype;
  lang: Lang;
  now: Date;
}

export interface Judge {
  judge(input: JudgeInput): Promise<Judgement>;
}

// 목 전용 범위-밖(슬픔) 근사 감지. 실제 감지는 AI가 한다(이건 개발용 대충).
const SAD_HINTS =
  /슬프|슬퍼|우울|외로|눈물|보고\s*싶|그리워|그립|허전|공허|sad|depress|lonely|miss you|grief|hopeless|empty inside|悲し|さみし|さびし|寂し|孤独|泣きたい|むなしい|空虚|落ち込/i;

function mockOffScope(text: string): boolean {
  return SAD_HINTS.test(text);
}

// 분석형 멘트 빌더(목 전용). 실제 스탯에 과장된 분석 프레임을 씌운다(§10 분석 톤).
// 실제 출시는 AI 런타임 생성 — 이건 프리뷰/오프라인용 근사.
type Tri = { ko: string; en: string; ja: string };
function analytical(s: RawSignals): { headline: string; notes: Tri[] } {
  const secs = Math.max(1, Math.round(s.durationMs / 1000));
  const m = Math.floor(secs / 60);
  const ss = String(secs % 60).padStart(2, "0");
  const mmss: Tri = {
    ko: m > 0 ? `${m}분 ${ss}초` : `${secs}초`,
    en: m > 0 ? `${m} minutes ${ss}` : `${secs}s`,
    ja: m > 0 ? `${m}分${ss}秒` : `${secs}秒`,
  };
  const bs = s.deleteCount;
  const tf = Math.max(0, Math.round(s.startDelayMs / 1000));
  const spd = Math.min(999, s.durationMs > 0 ? Math.round(s.insertCount / (s.durationMs / 60000)) : 0);
  const ex = s.exclaimCount;

  // 우선순위 후보(먼저 맞는 게 헤드라인, 나머지로 노트 채움).
  // ★각 줄은 '숫자 지우기 테스트' 통과(숫자를 지워도 뜻이 남는다). 공허한 분류·수치 되읽기 금지.
  const pool: { ok: boolean; line: Tri }[] = [
    {
      ok: secs < 8,
      line: {
        ko: `${secs}초 만에 끝. 화도 어지간히 바빴나 봐요.`,
        en: `done in ${secs}s. even the rage was in a hurry.`,
        ja: `${secs}秒で終了。怒りも気が短い。`,
      },
    },
    {
      ok: bs >= 5,
      line: {
        ko: `백스페이스 ${bs}번. 썼다 지웠다, 검열관이 따로 없네요.`,
        en: `${bs} backspaces. you censored yourself harder than they ever could.`,
        ja: `バックスペース${bs}回。誰よりも自分を検閲してる。`,
      },
    },
    {
      ok: tf >= 10,
      line: {
        ko: `시작까지 ${tf}초. 쓰기도 전에 시간이 다 갔네요.`,
        en: `${tf}s before the first key. the hesitation outlasted the rage.`,
        ja: `書き出しまで${tf}秒。書く前に時間が尽きた。`,
      },
    },
    {
      ok: spd >= 300,
      line: {
        ko: `분당 ${spd}타. 손이 생각보다 빨랐네요.`,
        en: `${spd}/min. your hands outran your head.`,
        ja: `毎分${spd}打。手が頭より先に動いた。`,
      },
    },
    {
      ok: secs > 120,
      line: {
        ko: `${mmss.ko} 동안 쏟아냈네요. 이쯤이면 화가 취미예요.`,
        en: `${mmss.en} of this. at this point it's a hobby.`,
        ja: `${mmss.ja}も。もう趣味の域ですね。`,
      },
    },
    {
      ok: ex >= 3,
      line: {
        ko: `느낌표 ${ex}개. 마침표로는 분이 안 풀렸나 봐요.`,
        en: `${ex} exclamation marks. periods just weren't going to cut it.`,
        ja: `感嘆符${ex}個。句点じゃ足りなかった。`,
      },
    },
    {
      ok: true, // 기본값(튀는 수치가 없을 때) — 공허한 분류 대신 담백한 데드팬
      line: {
        ko: `특이사항 없음. 무난하게 한 건 하셨네요.`,
        en: `nothing flagged. a perfectly ordinary blowup.`,
        ja: `特記事項なし。ごく普通の爆発でした。`,
      },
    },
  ];
  const matched = pool.filter((p) => p.ok).map((p) => p.line);
  return { headline: "", notes: matched }; // headline은 호출부에서 lang 적용
}

/** content_factor 휴리스틱(목 전용). 난타/반복 감지해 깎음. */
function mockContentFactor(s: RawSignals): number {
  const tokens = s.text.trim().split(/\s+/).filter(Boolean).length;
  const uniqueRatio =
    tokens > 0 ? new Set(s.text.trim().split(/\s+/)).size / tokens : 0;
  // 난타·도배: 토큰 거의 없거나 반복 심함.
  if (tokens < 2 || uniqueRatio < 0.3) return 0.1;
  if (tokens < 5) return 0.6; // 약한 진짜
  // 감정 강도 높으면 격렬 쪽.
  const intense = s.exclaimCount + s.capsRun * 0.5 > 5;
  if (intense && tokens >= 12) return 1.15;
  return 0.95; // 보통
}

/** 목 판정: 실제 AI 없이 그럴듯한 데드팬 결과 생성(개발용). */
export const mockJudge: Judge = {
  async judge(input: JudgeInput): Promise<Judgement> {
    const { signals: s, lang } = input;
    // 우선순위: crisis > off_scope > 일반.
    // 목에서는 로컬 트리거를 crisis로 간주(실제로는 AI 2차가 맥락 확정).
    if (localCrisisTrigger(s.text)) {
      return { content_factor: 0, crisis: true, off_scope: false, headline: "", archetype_line: "", report_notes: [], step_back: "" };
    }
    // 분노가 아닌 슬픔·우울이면 채점 안 함. 종이는 버려주고 step_back 한 줄만.
    if (mockOffScope(s.text)) {
      return { content_factor: 0, crisis: false, off_scope: true, headline: "", archetype_line: "", report_notes: [], step_back: STEP_BACK[lang] };
    }
    // 분석형 멘트(§10): 헤드라인 = 가장 두드러진 스탯, 노트 = 그다음 2개.
    const { notes: cards } = analytical(s);
    const headline = (cards[0] ?? cards[cards.length - 1])[lang];
    const report_notes = cards.slice(1, 3).map((c) => c[lang]);
    return {
      content_factor: mockContentFactor(s),
      crisis: false,
      off_scope: false,
      headline, // 실제 출시는 AI 런타임 생성
      archetype_line: "",
      report_notes,
      step_back: "",
    };
  },
};

/** 오프라인/실패 폴백 (§12): 데드팬 프리셋 + 행동-only(content_factor=1). */
export function fallbackJudgement(_archetype: Archetype, lang: Lang): Judgement {
  return {
    content_factor: 1,
    crisis: false,
    off_scope: false,
    step_back: "",
    headline: {
      ko: "판정 불가. 너무 빡쳐서 서버가 도망갔습니다.",
      en: "Verdict unavailable. You scared the server off.",
      ja: "判定不能。怒りすぎてサーバーが逃げました。",
    }[lang],
    archetype_line: "",
    report_notes: [
      {
        ko: "오프라인 모드. 행동 점수만 셌습니다.",
        en: "Offline. Behavior score only.",
        ja: "オフライン。行動スコアのみ。",
      }[lang],
    ],
    fallback: true,
  };
}

// 실제 Vertex AI(Gemini) 호출은 httpJudge.ts(클라이언트) → server/judge.ts(프록시)로 분리.
// 클라이언트는 절대 키를 들지 않는다. 호출부(verdict)에서 실패 시 fallbackJudgement로 폴백.
